import React from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import SEO from "../components/seo/SEO";
import { ArrowRight, Snowflake, ThermometerSun, Wind, Shield } from "lucide-react";
import { openOpenSolar } from "../lib/widgets";
import { track } from "../lib/analytics";

const faqs = [
  { q: "Kan een airco ook verwarmen?", a: "Ja. Een moderne airco is een lucht-lucht warmtepomp die efficiënt kan koelen én verwarmen. In voor- en naseizoen verwarmt u zuinig zonder gas." },
  { q: "Hoeveel verbruikt een airco?", a: "Dat hangt af van vermogen, gebruik en isolatie. Onze A+++ systemen leveren veel comfort tegen een laag verbruik dankzij hoge COP/SCOP-waarden." },
  { q: "Maakt een airco veel geluid?", a: "Binnenunits zijn fluisterstil, met nachtstand. Buitenunits plaatsen we trillingsvrij en strategisch om geluid te minimaliseren." },
  { q: "Welk vermogen heb ik nodig?", a: "Richtwaarde: ca. 30–60 W per m³, afhankelijk van isolatie en ligging. We adviseren precies op basis van uw woning." },
  { q: "Heb ik een vergunning nodig?", a: "Meestal niet voor een buitenunit op eigen terrein. In bijzondere situaties (monument, VvE) adviseren we graag." },
  { q: "Hoe vaak onderhoud?", a: "Reinig filters maandelijks en plan jaarlijks onderhoud voor maximale efficiëntie en hygiëne." },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Airco installatie en onderhoud",
    provider: { "@type": "Organization", name: "SOL-VI" },
    areaServed: "Nederland",
    serviceType: "Airconditioning (lucht-lucht warmtepomp)",
    description:
      "Comfortabel koelen én verwarmen met energiezuinige airco’s. Vakkundige montage, strak afgewerkt.",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

export default function Airco() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Airco: koelen en verwarmen | SOL-VI"
          description="Comfortabel en energiezuinig koelen én verwarmen met moderne airco’s (lucht-lucht warmtepompen). Vakkundige montage en strak afgewerkt."
          type="article"
          jsonLd={jsonLd}
        />

        <section className="pt-20 pb-12">
          <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Airco: koel in de zomer, warm in de winter</h1>
              <p className="text-lg text-muted-foreground mb-6">
                Energiezuinige lucht-lucht warmtepompen voor comfortabel koelen én verwarmen. Fluisterstil,
                strak afgewerkt en slim te bedienen met app of WiFi.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="cta"
                  onClick={() => {
                    track("airco_cta_offerte");
                    openOpenSolar();
                  }}
                >
                  Gratis Offerte
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    track("airco_cta_calendly");
                    (window as any).Calendly?.initPopupWidget?.({
                      url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800",
                    });
                  }}
                >
                  Plan adviesgesprek
                </Button>
              </div>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2"><Snowflake className="h-4 w-4 text-primary" /> Koelen & verwarmen</div>
                <div className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-primary" /> A+++ efficiëntie</div>
                <div className="flex items-center gap-2"><Wind className="h-4 w-4 text-primary" /> Fluisterstil</div>
                <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Strak afgewerkt</div>
              </div>
            </div>
            <div>
              <img
                src="/lovable-uploads/airco-hero.png"
                alt="Airco binnenunit voor koelen en verwarmen - SOL-VI"
                className="w-full h-auto rounded-lg shadow-lg"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = "/src/assets/warmtepomp.jpg";
                }}
              />
            </div>
          </div>
        </section>

        <section className="py-12 border-t border-border/60">
          <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10 items-start">
            <article>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Welke airco-oplossing past bij u?</h2>
              <p className="text-muted-foreground mb-4">
                Van één ruimte tot het hele huis: we leveren single-split en multi-split systemen met
                het juiste vermogen. We adviseren over plaatsing van binnen- en buitenunits, leidinglengte
                en condensafvoer voor een nette, duurzame installatie.
              </p>
              <ul className="space-y-2 list-disc pl-5 text-muted-foreground">
                <li>Single-split: één binnenunit voor slaapkamer, woonkamer of kantoor</li>
                <li>Multi-split: meerdere binnenunits op één buitenunit</li>
                <li>Capaciteitsadvies op basis van volume, isolatie en ligging</li>
                <li>Discrete montage, strak leidingwerk en nette afwerking</li>
              </ul>
            </article>
            <aside className="rounded-lg bg-muted/30 p-6">
              <h3 className="font-semibold mb-2">Waarom SOL-VI</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Snowflake className="h-4 w-4 text-primary" /> Comfort het hele jaar</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-primary" /> Zeer energiezuinig</li>
                <li className="flex items-center gap-2"><Wind className="h-4 w-4 text-primary" /> Fluisterstil en schoon</li>
                <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Vakkundige montage en service</li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Ons installatieproces</h2>
            <div className="grid md:grid-cols-3 gap-6 text-muted-foreground">
              <div className="rounded-lg border border-border p-5">
                <h4 className="font-semibold mb-1 text-foreground">1. Schouw & advies</h4>
                <p>We inventariseren uw wensen en bekijken de locatie. U krijgt een helder advies en voorstel.</p>
              </div>
              <div className="rounded-lg border border-border p-5">
                <h4 className="font-semibold mb-1 text-foreground">2. Montage</h4>
                <p>Vakkundige plaatsing van binnen- en buitenunit, netjes leidingwerk en elektrische aansluiting.</p>
              </div>
              <div className="rounded-lg border border-border p-5">
                <h4 className="font-semibold mb-1 text-foreground">3. Inbedrijfstelling & uitleg</h4>
                <p>We testen het systeem en stellen het in. U krijgt uitleg over bediening en onderhoud.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-t border-border/60">
          <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10 items-start">
            <article>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Onderhoud & hygiëne</h2>
              <p className="text-muted-foreground mb-4">
                Regelmatige reiniging en periodiek onderhoud zorgen voor optimale prestaties en een gezond
                binnenklimaat. We bieden onderhoud en service op maat.
              </p>
              <ul className="space-y-2 list-disc pl-5 text-muted-foreground">
                <li>Filters reinigen of vervangen</li>
                <li>Controle van koelleidingen en aansluitingen</li>
                <li>Prestaties, geluid en instellingen checken</li>
              </ul>
            </article>
            <aside className="rounded-lg bg-muted/30 p-6">
              <h3 className="font-semibold mb-2">Vragen?</h3>
              <p className="text-sm text-muted-foreground mb-4">We adviseren u graag over het juiste systeem en vermogen.</p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="cta"
                  onClick={() => {
                    track("airco_cta_offerte_mid");
                    openOpenSolar();
                  }}
                >
                  Gratis Offerte
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    track("airco_cta_calendly_mid");
                    (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" });
                  }}
                >
                  Plan gesprek
                </Button>
              </div>
            </aside>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Veelgestelde vragen</h2>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-lg border border-border p-4">
                  <summary className="cursor-pointer list-none font-medium text-foreground">{f.q}</summary>
                  <p className="mt-2 text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 border-t border-border/60">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Klaar voor comfort het hele jaar?</h2>
            <p className="text-muted-foreground mb-6">Vraag een vrijblijvende offerte aan of plan direct een adviesgesprek.</p>
            <div className="flex justify-center gap-3">
              <Button
                variant="cta"
                onClick={() => {
                  track("airco_cta_offerte_bottom");
                  openOpenSolar();
                }}
              >
                Gratis Offerte
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  track("airco_cta_calendly_bottom");
                  (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" });
                }}
              >
                Plan adviesgesprek
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
